// src/user/utils/taskStorage.js

/**
 * Task-scoped localStorage helpers for workflow snapshots, form definitions, and drafts.
 * We DO NOT reuse across tasks; everything is namespaced by (accessKey, taskId).
 */

const ROOT = 'wf'; // root namespace

// ---------- Key builders ----------
export function nsRoot(accessKey, taskId) {
  return `${ROOT}::${accessKey}::${taskId}::`;
}

export function keyWorkflow(accessKey, taskId) {
  return `${nsRoot(accessKey, taskId)}definition`;
}

export function keyFormDef(accessKey, taskId, stepId, slug) {
  return `${nsRoot(accessKey, taskId)}form::${stepId}::${slug}`;
}

export function keyDraft(accessKey, taskId, stepId, slug) {
  return `${nsRoot(accessKey, taskId)}draft::${stepId}::${slug}`;
}

export const PHOTO_CAPTURE_DRAFT_SLUG = '_photo_capture';

export function keyPhotoDraft(accessKey, taskId, stepId) {
  return keyDraft(accessKey, taskId, stepId, PHOTO_CAPTURE_DRAFT_SLUG);
}

export function keyTrajectory(accessKey, taskId) {
  return `${nsRoot(accessKey, taskId)}trajectory`;
}

const TRAJECTORY_SCHEMA_VERSION = 1;

/**
 * Upper bound on the recorded transitions for one task.
 *
 * Every step change appends an entry and the whole trajectory is rewritten to
 * localStorage, which is shared across every task a device has open. The array had no
 * bound, so a workflow that loops -- or a long session on a device that never clears its
 * drafts -- grew it indefinitely. The failure is quiet when it comes: setJSON swallows
 * quota errors, so the first thing lost is draft answers, not this.
 *
 * 500 is far past any genuine path through a workflow (roughly 60 kB), so reaching it
 * means something is looping rather than that a real path was that long. The oldest
 * entries are dropped, keeping the steps nearest the outcome.
 */
export const MAX_TRAJECTORY_TRANSITIONS = 500;

export function createEmptyTrajectory() {
  return {
    schema_version: TRAJECTORY_SCHEMA_VERSION,
    visits: {},
    transitions: [],
  };
}

// ---------- JSON get/set ----------
function getJSON(key) {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function setJSON(key, value) {
  try {
    if (value === null || value === undefined) {
      localStorage.removeItem(key);
      return;
    }
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // ignore quota errors silently
  }
}

// ---------- Workflow snapshot ----------
export function saveWorkflowSnapshot(accessKey, taskId, workflowSnapshot) {
  setJSON(keyWorkflow(accessKey, taskId), {
    steps: normalizeSteps(workflowSnapshot),
    snapshotAt: new Date().toISOString(),
  });
}

export function getWorkflowSnapshot(accessKey, taskId) {
  return getJSON(keyWorkflow(accessKey, taskId));
}

// Accepts array or { steps: [...] }
function normalizeSteps(workflowSteps) {
  if (!workflowSteps) return [];
  if (Array.isArray(workflowSteps)) return workflowSteps;
  if (typeof workflowSteps === 'object' && Array.isArray(workflowSteps.steps)) {
    return workflowSteps.steps;
  }
  return [];
}

// Extract [{ stepId, slug }] for all form steps
export function extractFormRefs(workflowSnapshot) {
  const steps = normalizeSteps(workflowSnapshot?.steps || workflowSnapshot);
  const out = [];
  steps.forEach((s) => {
    if (s && (s.type || 'html').toLowerCase() === 'form' && s.slug) {
      out.push({ stepId: String(s.id || ''), slug: String(s.slug) });
    }
  });
  return out;
}

// ---------- Form definitions ----------
export function saveFormDef(accessKey, taskId, stepId, slug, def) {
  // def: { schema, uiSchema, defaults, title, description, meta, slug }
  setJSON(keyFormDef(accessKey, taskId, stepId, slug), def);
}

export function getFormDef(accessKey, taskId, stepId, slug) {
  return getJSON(keyFormDef(accessKey, taskId, stepId, slug));
}

// ---------- Draft answers ----------
export function saveDraft(accessKey, taskId, stepId, slug, answers) {
  setJSON(keyDraft(accessKey, taskId, stepId, slug), answers || {});
}

export function getDraft(accessKey, taskId, stepId, slug) {
  return getJSON(keyDraft(accessKey, taskId, stepId, slug)) || null;
}

export function getPhotoDraft(accessKey, taskId, stepId) {
  return getJSON(keyPhotoDraft(accessKey, taskId, stepId));
}

export function savePhotoDraft(accessKey, taskId, stepId, capture) {
  setJSON(keyPhotoDraft(accessKey, taskId, stepId), capture);
}

export function getTrajectory(accessKey, taskId) {
  return getJSON(keyTrajectory(accessKey, taskId)) || createEmptyTrajectory();
}

export function saveTrajectory(accessKey, taskId, trajectory) {
  setJSON(keyTrajectory(accessKey, taskId), trajectory);
}

export function recordStepVisit(trajectory, stepId, at = new Date().toISOString()) {
  const next = {
    ...trajectory,
    visits: { ...(trajectory?.visits || {}) },
    transitions: [...(trajectory?.transitions || [])],
  };

  const existing = next.visits[stepId];
  if (!existing) {
    next.visits[stepId] = {
      first_visited_at: at,
      last_visited_at: at,
      visit_count: 1,
    };
  } else {
    next.visits[stepId] = {
      ...existing,
      last_visited_at: at,
      visit_count: (existing.visit_count || 0) + 1,
    };
  }

  return next;
}

export function recordTransition(
  trajectory,
  { from, to, actionIndex, actionText, at = new Date().toISOString() }
) {
  const appended = [
    ...(trajectory?.transitions || []),
    {
      from,
      to,
      action_index: actionIndex,
      action_text: actionText || '',
      at,
    },
  ];
  const overflow = Math.max(0, appended.length - MAX_TRAJECTORY_TRANSITIONS);

  return {
    ...trajectory,
    visits: { ...(trajectory?.visits || {}) },
    transitions: overflow > 0 ? appended.slice(overflow) : appended,
    // Carried into the work report so the admin path view can say the record is partial
    // rather than present a truncated path as if it were the whole one.
    transitions_dropped: (trajectory?.transitions_dropped || 0) + overflow,
  };
}

// ---------- Namespace ops ----------
function listNamespaceKeys(accessKey, taskId) {
  const prefix = nsRoot(accessKey, taskId);
  const keys = [];
  for (let i = 0; i < localStorage.length; i += 1) {
    const k = localStorage.key(i);
    if (k && k.startsWith(prefix)) keys.push(k);
  }
  return keys;
}

export function clearTaskNamespace(accessKey, taskId) {
  const keys = listNamespaceKeys(accessKey, taskId);
  keys.forEach((k) => {
    try {
      localStorage.removeItem(k);
    } catch {
      // ignore
    }
  });
}

const ORPHAN_NAMESPACE_TTL_MS = 30 * 24 * 60 * 60 * 1000;

/**
 * Remove wf:: task namespaces whose workflow snapshot is missing or older than maxAgeMs.
 */
export function sweepOrphanedTaskNamespaces(maxAgeMs = ORPHAN_NAMESPACE_TTL_MS) {
  const namespaces = new Set();
  for (let i = 0; i < localStorage.length; i += 1) {
    const k = localStorage.key(i);
    if (!k || !k.startsWith(`${ROOT}::`)) continue;
    const parts = k.split('::');
    if (parts.length >= 3) {
      namespaces.add(`${parts[1]}::${parts[2]}`);
    }
  }

  const cutoff = Date.now() - maxAgeMs;
  namespaces.forEach((ns) => {
    const [accessKey, taskId] = ns.split('::');
    const snapshot = getWorkflowSnapshot(accessKey, taskId);
    const snapshotAt = snapshot?.snapshotAt
      ? new Date(snapshot.snapshotAt).getTime()
      : null;
    if (snapshotAt == null || snapshotAt < cutoff) {
      clearTaskNamespace(accessKey, taskId);
    }
  });
}

// Iterate drafts in this task: yields { stepId, slug, answers }
export function* iterateDrafts(accessKey, taskId) {
  const prefix = `${nsRoot(accessKey, taskId)}draft::`;
  const all = listNamespaceKeys(accessKey, taskId);
  for (const k of all) {
    if (k.startsWith(prefix)) {
      // k format: wf::<ak>::<taskId>::draft::<stepId>::<slug>
      const parts = k.split('::');
      const stepId = parts[4] || '';
      const slug = parts[5] || '';
      const answers = getJSON(k) || {};
      yield { stepId, slug, answers };
    }
  }
}
